import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { NotifyPhaserEvents } from 'Phaser/EventBus2';
import { useHardwareBack } from 'Hooks/useHardwareBack';

/**
 * The tutorial's Back: the header's on-screen Back arrow, and Android's hardware or gesture Back
 * while the tutorial is on screen. The header's Back unloads the exercise and leaves for the
 * roadmap. On the older page-by-page tutorial, once past its first page, the footer's Back steps
 * back one page, so hardware Back does that instead while the footer's Back is shown.
 *
 * Returns the header's Back.
 */
export const useTutorialBack = ({
  pageBackShown,
  backOnePage,
}: {
  pageBackShown: boolean;
  backOnePage: () => void;
}): (() => void) => {
  const navigate = useNavigate();
  // Set once Back has asked for the roadmap: a second press before the roadmap renders does not
  // navigate again.
  const leavingRef = useRef(false);

  const leaveForRoadmap = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    console.log('[Tutorial] Back button clicked - cleaning up before navigation');
    // Send unload event to Phaser to clean up the current scene
    NotifyPhaserEvents.UnloadExercise();
    console.log('[Tutorial] Sent UnloadExercise event to Phaser');
    // Small delay to allow cleanup to complete before navigation
    setTimeout(() => {
      console.log('[MUS-61] Tutorial backToRoadmap → /roadmap');
      navigate('/roadmap');
    }, 100);
  }, [navigate]);

  useHardwareBack('/tutorial', () => (pageBackShown ? backOnePage() : leaveForRoadmap()));

  return leaveForRoadmap;
};
